import { useMemo, useState } from "react";
import { Alert, Button, Checkbox, Modal, Tooltip, Typography } from "antd";
import { ControlOutlined } from "@ant-design/icons";
import { useDashboard } from "../Contexts/dashboardHooks";
import { useDataPipeline } from "../DataPipeline";
import { DateRangeForm, DATE_RANGE_DEFAULTS } from "../AddWidgetModal/shared/DateRangeForm";
import { PlayersForm, PLAYERS_DEFAULTS, resolveSubjectNames } from "../AddWidgetModal/shared/PlayersForm";
import type { DateRangeFormFields } from "../AddWidgetModal/shared/DateRangeForm";
import type { PlayersFormFields } from "../AddWidgetModal/shared/PlayersForm";
import type { ComponentSpec } from "../Processing/types";
import { getWidgetDef } from "../widgetRegistry";
import { NoControlPanel } from "../widgetContract";
import type { ControlPanelHandler, ControlPanelPatch } from "../widgetContract";
import type { ControllerKind } from "../Processing/types";
import {
  bindableMiscs,
  effectiveBinding,
  eligibleByKind,
  listControllers,
} from "../Processing/controllerBindings";

const { Text } = Typography;

export function BulkTableFiltersButton() {
  const { state, bulkEditWidgets } = useDashboard();
  const pipeline = useDataPipeline();

  const [open, setOpen] = useState(false);
  const [applyDate, setApplyDate] = useState(true);
  const [applySubjects, setApplySubjects] = useState(true);
  const [dateFields, setDateFields] = useState<DateRangeFormFields>(DATE_RANGE_DEFAULTS);
  const [playerFields, setPlayerFields] = useState<PlayersFormFields>(PLAYERS_DEFAULTS);

  // Which widgets the Control Panel bulk-applies to is the registry's answer: any widget
  // whose definition declares a real `controlPanel` handler (not the explicit
  // NoControlPanel opt-out). Adding a handler to a definition enrolls it here — no list
  // to keep in sync. Per-type nuances (radar = date only; body_map excluded) live in the
  // handlers / their NoControlPanel opt-out.
  const affectedWidgets = Object.values(state.widgets).filter((w) => {
    const cp = w.spec && getWidgetDef(w.spec.type)?.controlPanel;
    return cp != null && cp !== NoControlPanel;
  });
  const affectedCount = affectedWidgets.length;

  // A section that follows a Slicer keeps following it: the bulk apply skips it
  // and says so, rather than writing a value the controller would immediately override.
  const controllerIds = useMemo<Record<ControllerKind, Set<string>>>(
    () => eligibleByKind(listControllers(Object.values(state.widgets))),
    [state.widgets],
  );

  const skippedSectionCount = useMemo(() => {
    const kinds: ControllerKind[] = [
      ...(applyDate ? (["date"] as const) : []),
      ...(applySubjects ? (["subject"] as const) : []),
    ];
    let count = 0;
    for (const widget of affectedWidgets) {
      for (const misc of bindableMiscs(widget.spec)) {
        for (const kind of kinds) {
          if (effectiveBinding(misc, kind, controllerIds[kind])) count += 1;
        }
      }
    }
    return count;
  }, [affectedWidgets, controllerIds, applyDate, applySubjects]);

  const handleOpen = () => {
    setApplyDate(true);
    setApplySubjects(true);
    setDateFields(DATE_RANGE_DEFAULTS);
    setPlayerFields(PLAYERS_DEFAULTS);
    setOpen(true);
  };

  const handleApply = () => {
    const patch: ControlPanelPatch = {
      applyDate,
      applySubjects,
      date: dateFields,
      subjects: {
        selectedSubjects: playerFields.selectedSubjects,
        selectedDrills: playerFields.selectedDrills,
        subjectActiveTab: playerFields.subjectActiveTab,
        selectedSubjectNames: resolveSubjectNames(
          playerFields.selectedSubjects,
          playerFields.selectedDrills,
          pipeline.getSubjectEntities,
        ),
      },
      controllers: controllerIds,
    };

    const updates: Array<{ widgetId: string; spec: ComponentSpec }> = [];
    for (const widget of affectedWidgets) {
      if (!widget.spec) continue;
      const cp = getWidgetDef(widget.spec.type)?.controlPanel;
      if (!cp || cp === NoControlPanel) continue;
      // Handlers mutate; clone so the live state spec is untouched until bulkEdit commits.
      const clone = JSON.parse(JSON.stringify(widget.spec)) as ComponentSpec;
      (cp as ControlPanelHandler)(clone, patch);
      updates.push({ widgetId: widget.id, spec: clone });
    }

    bulkEditWidgets(updates);
    setOpen(false);
  };

  const appliedFilters = [applyDate && "date range", applySubjects && "subjects"]
    .filter(Boolean)
    .join(" and ");

  const handleConfirm = () => {
    Modal.confirm({
      title: "Apply to All Widgets",
      content: (
        <span>
          This will overwrite the {appliedFilters} on{" "}
          <strong>{affectedCount}</strong> widget
          {affectedCount !== 1 ? "s" : ""}. Individual widget settings will be lost.
          <br />
          <br />
          {skippedSectionCount > 0 && (
            <>
              <br />
              <br />
              {skippedSectionCount} widget filter section
              {skippedSectionCount !== 1 ? "s" : ""} follow a Slicer and will not
              be changed.
            </>
          )}
          <br />
          <br />
          Are you sure you want to continue?
        </span>
      ),
      okText: "Apply to All",
      okType: "primary",
      cancelText: "Cancel",
      onOk: handleApply,
    });
  };

  return (
    <>
      <Tooltip title="Control panel">
        <Button
          type="text"
          icon={<ControlOutlined />}
          onClick={handleOpen}
          className="dashboard-toolbar-icon-btn"
          disabled={affectedCount === 0}
        />
      </Tooltip>

      <Modal
        open={open}
        title="Control Panel"
        onCancel={() => setOpen(false)}
        width={520}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleConfirm}
            disabled={affectedCount === 0 || (!applyDate && !applySubjects)}
          >
            Apply to All Widgets ({affectedCount})
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Alert
            type="warning"
            showIcon
            message={
              <Text style={{ fontSize: 12 }}>
                Applying will overwrite the date range and subject filters on all{" "}
                <strong>{affectedCount}</strong> widget
                {affectedCount !== 1 ? "s" : ""} on this dashboard. Each widget's
                other settings (columns, grouping, etc.) will not be changed.
              </Text>
            }
          />

          {skippedSectionCount > 0 && (
            <Alert
              type="info"
              showIcon
              message={
                <Text style={{ fontSize: 12 }}>
                  <strong>{skippedSectionCount}</strong> widget filter section
                  {skippedSectionCount !== 1 ? "s" : ""} follow a Slicer and were
                  not changed. Remove the binding on a widget to bulk-apply to it again.
                </Text>
              }
            />
          )}

          <div style={{ opacity: applyDate ? 1 : 0.4, pointerEvents: applyDate ? "auto" : "none" }}>
            <Checkbox
              checked={applyDate}
              onChange={(e) => setApplyDate(e.target.checked)}
              style={{ pointerEvents: "auto", opacity: 1, marginBottom: 8 }}
            >
              <Text strong style={{ fontSize: 12 }}>Apply date range</Text>
            </Checkbox>
            <DateRangeForm
              value={dateFields}
              onChange={(patch) => setDateFields((prev) => ({ ...prev, ...patch }))}
              description="Set the date range to apply to all widgets."
            />
          </div>

          <div style={{ opacity: applySubjects ? 1 : 0.4, pointerEvents: applySubjects ? "auto" : "none" }}>
            <Checkbox
              checked={applySubjects}
              onChange={(e) => setApplySubjects(e.target.checked)}
              style={{ pointerEvents: "auto", opacity: 1, marginBottom: 8 }}
            >
              <Text strong style={{ fontSize: 12 }}>Apply subjects</Text>
            </Checkbox>
            <PlayersForm
              value={playerFields}
              onChange={(patch) => setPlayerFields((prev) => ({ ...prev, ...patch }))}
              getSubjectEntities={pipeline.getSubjectEntities}
              description="Set the subjects to apply to all widgets."
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
